import axios from "axios";
import { defineStore } from "pinia";
import { FRAPPE_BASE_URL } from "@/services/frappe/config";
import {
  bindBrowserReachabilityEvents,
  getReachabilitySnapshot,
  isBrowserOnline,
  markServerReachable,
  markServerUnreachable,
  subscribeReachability,
} from "@/services/offlineSignal";

/**
 * The reactive face of `services/offlineSignal` for components.
 *
 * The signal module stays plain (no Vue, no Pinia) because axios interceptors,
 * the router guard and `permissionHelper` all read it before Pinia exists. This
 * store mirrors it into reactive state and owns the one thing the signal cannot
 * do on its own: actively re-check a server that stopped answering.
 */

/** How often to re-check while we believe the server is unreachable. */
const OFFLINE_PROBE_INTERVAL_MS = 15000;

/**
 * Frappe's built-in whitelisted no-op. It is the cheapest possible "are you
 * there?" — no doctype read, no permission check, and it answers for a logged
 * out session too, which is what we want: this asks about the CONNECTION, not
 * about the session.
 */
const PING_ENDPOINT = "/api/method/ping";

/** A probe must never outlive its own interval, or probes pile up on a dead link. */
const PING_TIMEOUT_MS = 8000;

/**
 * A BARE client, deliberately not `axiosInstance`.
 *
 * The shared instance carries the app's interceptors, and one of them turns a 401
 * into `handleSessionExpired()` — logout, cleared tokens, redirect. A liveness
 * probe must never be able to end a session: it asks about the wire, not about
 * the user, and it runs unattended every 15 seconds while offline. It also skips
 * the auth header entirely, so there is nothing to refuse.
 *
 * The one thing we lose by bypassing the interceptors is their reachability
 * bookkeeping, so `probe()` records the verdict itself.
 */
const probeClient = axios.create({
  baseURL: FRAPPE_BASE_URL,
  timeout: PING_TIMEOUT_MS,
  withCredentials: false,
  headers: { Accept: "application/json" },
});

let probeTimer: number | null = null;
let unsubscribe: (() => void) | null = null;
let probeInFlight = false;

const useOfflineStore = defineStore("offline", {
  state: () => {
    const snapshot = getReachabilitySnapshot();

    return {
      browserOnline: snapshot.browserOnline,
      serverReachable: snapshot.serverReachable,
      lastOkAt: snapshot.lastOkAt,
      lastFailureAt: snapshot.lastFailureAt,
      /** True while a connection probe is in flight (for a spinner, not a blocker). */
      checking: false,
    };
  },

  getters: {
    /**
     * The single question the UI should ask. Deliberately NOT just
     * `!browserOnline`: a laptop connected to Wi-Fi with no route to the server
     * reports itself online while nothing works.
     */
    isOffline(state): boolean {
      return !state.serverReachable || !state.browserOnline;
    },

    isOnline(): boolean {
      return !this.isOffline;
    },
  },

  actions: {
    /** Pull the current signal state into reactive state. */
    sync() {
      const snapshot = getReachabilitySnapshot();

      this.browserOnline = snapshot.browserOnline;
      this.serverReachable = snapshot.serverReachable;
      this.lastOkAt = snapshot.lastOkAt;
      this.lastFailureAt = snapshot.lastFailureAt;
    },

    /**
     * Ask the server whether it is there. Resolves to the answer rather than
     * throwing, because every caller wants the boolean, not an error to handle.
     */
    async probe(): Promise<boolean> {
      if (probeInFlight)
        return !this.isOffline;

      // No link at all: the browser is right about the negative, so do not burn
      // a request (and, on mobile, a radio wake-up) to confirm it.
      if (!isBrowserOnline()) {
        markServerUnreachable("browser");
        this.sync();

        return false;
      }

      probeInFlight = true;
      this.checking = true;

      try {
        // Cache-busted: a proxy or the browser serving a cached 200 would report
        // a dead server as alive, which is the one answer that must never be wrong.
        await probeClient.get(PING_ENDPOINT, { params: { _: Date.now() } });
        markServerReachable();

        return true;
      }
      catch (error: any) {
        // ANY answer proves the wire works. A 401, 403, 404 or 500 all mean we
        // reached the server, and only the absence of a response means we did not.
        if (error?.response) {
          markServerReachable();

          return true;
        }

        markServerUnreachable(error?.code === "ECONNABORTED" ? "timeout" : "network");

        return false;
      }
      finally {
        probeInFlight = false;
        this.checking = false;
        this.sync();
      }
    },

    /**
     * Start mirroring the signal and probing while offline. Idempotent, so the
     * plugin and any component may both call it.
     */
    start() {
      bindBrowserReachabilityEvents();

      if (!unsubscribe) {
        unsubscribe = subscribeReachability(() => {
          this.sync();
        });
      }

      this.sync();

      if (typeof window === "undefined" || probeTimer !== null)
        return;

      probeTimer = window.setInterval(() => {
        // Only probe when we believe we are offline. While online, ordinary
        // traffic is a better and free liveness signal.
        if (this.isOffline)
          void this.probe();
      }, OFFLINE_PROBE_INTERVAL_MS);

      // Coming back to the app is the moment the user most wants a correct
      // answer, so check immediately rather than waiting for the next tick.
      window.addEventListener("online", () => {
        void this.probe();
      });
    },

    stop() {
      if (probeTimer !== null && typeof window !== "undefined") {
        window.clearInterval(probeTimer);
        probeTimer = null;
      }

      unsubscribe?.();
      unsubscribe = null;
    },
  },
});

export default useOfflineStore;
